import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout/Layout";
import OrderStatusTimeline from "../../components/GiftOrders/OrderStatusTimeline";
import OrderItemsTable from "../../components/GiftOrders/OrderItemsTable";
import OrderTotals from "../../components/GiftOrders/OrderTotals";
import BackLink from "../../components/Common/BackLink";
import LoadingState from "../../components/Common/LoadingState";
import { getOrderDetails } from "../../api/orderApi";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { showErrorToast } from "../../utils/toast";

const GiftOrderDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { orderId } = useParams();
    const [orderDetail, setOrderDetail] = useState(null);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const detailAction = useAsyncAction();
    const { run: runLoadOrderDetail } = detailAction;
    const isLoadingDetail = detailAction.isLoading;
    const apiError = detailAction.errorMessage;

    useEffect(() => {
        if (!apiError) return;
        showErrorToast(apiError);
    }, [apiError]);

    useEffect(() => {
        let mounted = true;
        if (!orderId) return;

        const loadDetail = async () => {
            const numericId = Number(orderId);
            const resolvedOrderId = Number.isFinite(numericId) ? numericId : orderId;

            const detail = await runLoadOrderDetail(
                () => getOrderDetails(resolvedOrderId),
                { errorFallback: t("GIFT_ORDER_DETAIL_ERROR_LOAD"), silentErrorToast: true }
            );

            if (!mounted) return;
            setOrderDetail(detail || null);
            setHasLoadedOnce(true);
        };

        loadDetail();
        return () => {
            mounted = false;
        };
    }, [orderId, runLoadOrderDetail, t]);

    return (
        <Layout activeLabel="Gift orders">
            <div>
                <BackLink label={t("GIFT_ORDER_DETAIL_BACK_TO_LIST")} onClick={() => navigate("/gift-orders")} />

                {isLoadingDetail && !hasLoadedOnce ? (
                    <LoadingState message={t("GIFT_ORDER_DETAIL_LOADING")} />
                ) : orderDetail ? (
                    <div>
                        <div className="mt-4">
                            <h1 className="text-lg font-semibold text-slate-900">
                                {orderDetail.displayId || `#${orderId}`}
                            </h1>
                            <p className="mt-1 text-sm text-slate-500">{t("GIFT_ORDER_DETAIL_SUBTITLE")}</p>
                        </div>
                        <OrderStatusTimeline current={orderDetail.statusKey} />
                        <OrderItemsTable items={orderDetail.items || []} />
                        <OrderTotals
                            itemsCount={(orderDetail.items || []).length}
                            subtotal={orderDetail.totalAmount}
                            totalCost={orderDetail.finalAmount || "€0.00"}
                        />
                    </div>
                ) : (
                    <div className="mt-8 rounded-md border border-slate-200 bg-input-bg px-6 py-8 text-sm text-slate-500">
                        {t("GIFT_ORDER_DETAIL_ERROR_EMPTY")}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default GiftOrderDetail;
